import React, { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext'; 
import { 
  User, Mail, Phone, MapPin, CreditCard, 
  Edit2, Save, Plus, Check, Trash2, 
  Loader2, CheckCircle, XCircle
} from 'lucide-react';
import { Helmet } from "react-helmet-async";

interface OperatorProfile {
  name: string;
  email: string;
  phone: string;
  address: string;
  deliveryAddress: string;
  isDefaultAddress: boolean;
}

interface PaymentCard {
  id: string;
  last4: string;
  brand: string;
  expiryMonth: string;
  expiryYear: string;
  isDefault: boolean;
}

const AccountPage = () => {
  const { user, setUser } = useApp(); 
  
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editAddressMode, setEditAddressMode] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  
  const [profile, setProfile] = useState<OperatorProfile>({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    deliveryAddress: '',
    isDefaultAddress: true
  });
  
  const [paymentCards, setPaymentCards] = useState<PaymentCard[]>([]);

  const [tempProfile, setTempProfile] = useState(profile);
  const [tempAddress, setTempAddress] = useState({
    address: profile.address,
    deliveryAddress: profile.deliveryAddress,
    isDefaultAddress: profile.isDefaultAddress
  });
  
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // New card form state
  const [newCard, setNewCard] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  });

  useEffect(() => {
    // Sync profile with user data when user changes
    if (user) {
      setProfile(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
    setTimeout(() => setLoading(false), 800);
  }, [user]);

  const handleEdit = () => {
    setTempProfile(profile);
    setEditMode(true);
  };

  const handleCancel = () => {
    setTempProfile(profile);
    setEditMode(false);
  };

  const handleSave = () => {
    // Update profile state
    setProfile(tempProfile);
    
    // Update user in context
    if (user) {
      setUser({
        ...user,
        name: tempProfile.name,
        email: tempProfile.email
      });
    }
    
    setEditMode(false);
    showMessage('success', '✨ Profile updated successfully!');
  };

  const handleEditAddress = () => {
    setTempAddress({
      address: profile.address,
      deliveryAddress: profile.deliveryAddress,
      isDefaultAddress: profile.isDefaultAddress
    });
    setEditAddressMode(true);
  };

  const handleCancelAddress = () => {
    setTempAddress({
      address: profile.address,
      deliveryAddress: profile.deliveryAddress,
      isDefaultAddress: profile.isDefaultAddress
    });
    setEditAddressMode(false);
  };

  const handleSaveAddress = () => {
    setProfile({
      ...profile,
      address: tempAddress.address,
      deliveryAddress: tempAddress.deliveryAddress,
      isDefaultAddress: tempAddress.isDefaultAddress
    });
    setEditAddressMode(false);
    showMessage('success', '📍 Address updated successfully!');
  };

  const handleMakeDefaultAddress = () => {
    setProfile({ ...profile, isDefaultAddress: true });
    showMessage('success', '📍 Address set as default delivery address');
  };

  const handleSetDefaultCard = (cardId: string) => {
    setPaymentCards(cards =>
      cards.map(card => ({
        ...card,
        isDefault: card.id === cardId
      }))
    );
    showMessage('success', '💳 Default payment method updated');
  };

  const handleDeleteCard = (cardId: string) => {
    const cardToDelete = paymentCards.find(card => card.id === cardId);
    
    if (cardToDelete?.isDefault && paymentCards.length > 1) {
      // If deleting default card, make another card default
      const remainingCards = paymentCards.filter(card => card.id !== cardId);
      setPaymentCards(
        remainingCards.map((card, index) => ({
          ...card,
          isDefault: index === 0
        }))
      );
    } else {
      setPaymentCards(cards => cards.filter(card => card.id !== cardId));
    }
    
    showMessage('success', '🗑️ Payment method removed');
  };

  const handleAddCard = () => {
    // Validate card inputs
    if (!newCard.cardNumber || !newCard.expiryDate || !newCard.cvv) {
      showMessage('error', '❌ Please fill in all card details');
      return;
    }

    // Basic validation
    if (newCard.cardNumber.replace(/\s/g, '').length < 13) {
      showMessage('error', '❌ Invalid card number');
      return;
    }

    if (!/^\d{2}\/\d{2}$/.test(newCard.expiryDate)) {
      showMessage('error', '❌ Invalid expiry date (use MM/YY format)');
      return;
    }

    if (newCard.cvv.length < 3) {
      showMessage('error', '❌ Invalid CVV');
      return;
    }

    // Extract last 4 digits
    const last4 = newCard.cardNumber.replace(/\s/g, '').slice(-4);
    
    // Determine card brand based on first digit
    let brand = 'Unknown';
    const firstDigit = newCard.cardNumber.replace(/\s/g, '')[0];
    if (firstDigit === '4') brand = 'Visa';
    else if (firstDigit === '5') brand = 'Mastercard';
    else if (firstDigit === '3') brand = 'Amex';

    // Parse expiry date
    const [month, year] = newCard.expiryDate.split('/');

    // Add new card
    const newCardData: PaymentCard = {
      id: Date.now().toString(),
      last4,
      brand,
      expiryMonth: month,
      expiryYear: `20${year}`,
      isDefault: paymentCards.length === 0 // First card is default
    };

    setPaymentCards([...paymentCards, newCardData]);
    
    // Reset form
    setNewCard({ cardNumber: '', expiryDate: '', cvv: '' });
    setShowAddCard(false);
    showMessage('success', '💳 Payment method added successfully');
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // Format card number input
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  // Format expiry date input
  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return `${v.slice(0, 2)}/${v.slice(2, 4)}`;
    }
    return v;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center px-4">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Account | KonectX</title>
        <meta name="description" content="Manage your KonectX account information and payment methods" />
      </Helmet>

   <div className="min-h-screen bg-gray-50 dark:bg-black pl-1 pr-6 sm:pl-2 sm:pr-8 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          {/* Message Toast */}
          {message && (
            <div className="mb-6 animate-in slide-in-from-top duration-300">
              <div
                className={`p-3 sm:p-4 rounded-xl flex items-center gap-2 sm:gap-3 ${
                  message.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
                }`}
              >
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span className="text-xs sm:text-sm font-medium">{message.text}</span>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                  Account Information
                </h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  Manage your personal details and preferences
                </p>
              </div>
              
              {!editMode && (
                <button
                  onClick={handleEdit}
                  className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-gray-900 dark:bg-blue-600 text-white rounded-xl hover:bg-gray-800 dark:hover:bg-blue-700 transition-all shadow-lg text-sm sm:text-base font-medium"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              )}

              {editMode && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={handleCancel}
                    className="flex-1 sm:flex-none px-4 py-2.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm sm:text-base font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium shadow-lg"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-5 sm:space-y-6">
            {/* Personal Information */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-500" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Personal Information</h2>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {/* Name */}
                <div>
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Name
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      value={tempProfile.name}
                      onChange={(e) => setTempProfile({ ...tempProfile, name: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 text-sm sm:text-base text-gray-900 dark:text-white transition-all"
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <p className="text-sm sm:text-base text-gray-900 dark:text-white font-medium px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-transparent">
                      {profile.name || 'Not provided'}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Phone
                  </label>
                  {editMode ? (
                    <input
                      type="tel"
                      value={tempProfile.phone}
                      onChange={(e) => setTempProfile({ ...tempProfile, phone: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 text-sm sm:text-base text-gray-900 dark:text-white transition-all"
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <p className="text-sm sm:text-base text-gray-900 dark:text-white font-medium px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-transparent">
                      {profile.phone || 'Not provided'}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Email
                  </label>
                  {editMode ? (
                    <input
                      type="email"
                      value={tempProfile.email}
                      disabled
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-xl text-sm sm:text-base text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      placeholder="Enter your email address"
                    />
                  ) : (
                    <p className="text-sm sm:text-base text-gray-900 dark:text-white font-medium px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-transparent">
                      {profile.email || 'Not provided'}
                    </p>
                  )}
                  {editMode && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
                      Email cannot be changed
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-purple-500/10 dark:bg-purple-500/20 flex items-center justify-center">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-500" />
                    </div>
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Address Details</h2>
                  </div>
                  
                  {!editAddressMode ? (
                    <button
                      onClick={handleEditAddress}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-xs sm:text-sm font-medium"
                    >
                      <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCancelAddress}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveAddress}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                      >
                        <Save className="w-3.5 h-3.5" />
                        Save
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {/* Primary Address */}
                <div>
                  <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Address
                  </label>
                  {editAddressMode ? (
                    <textarea
                      value={tempAddress.address}
                      onChange={(e) => setTempAddress({ ...tempAddress, address: e.target.value })}
                      rows={2}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 text-sm sm:text-base text-gray-900 dark:text-white transition-all resize-none"
                      placeholder="Enter your address"
                    />
                  ) : (
                    <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-transparent">
                      <p className="text-sm sm:text-base text-gray-900 dark:text-white font-medium">
                        {profile.address || 'Not provided'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Delivery Address */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Delivery Address
                    </label>
                    {!profile.isDefaultAddress && !editAddressMode && profile.deliveryAddress && (
                      <button
                        onClick={handleMakeDefaultAddress}
                        className="px-3 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                      >
                        Make Default
                      </button>
                    )}
                  </div>
                  {editAddressMode ? (
                    <textarea
                      value={tempAddress.deliveryAddress}
                      onChange={(e) => setTempAddress({ ...tempAddress, deliveryAddress: e.target.value })}
                      rows={2}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 text-sm sm:text-base text-gray-900 dark:text-white transition-all resize-none"
                      placeholder="Enter your delivery address"
                    />
                  ) : (
                    <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-transparent">
                      <p className="text-sm sm:text-base text-gray-900 dark:text-white font-medium mb-2">
                        {profile.deliveryAddress || 'Not provided'}
                      </p>
                      {profile.isDefaultAddress && profile.deliveryAddress && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 dark:bg-green-500/20 text-green-700 dark:text-green-400 text-xs font-semibold rounded-lg">
                          <Check className="w-3 h-3" />
                          Default Delivery Address
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center">
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-500" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Payment Details</h2>
                </div>
                <button
                  onClick={() => setShowAddCard(!showAddCard)}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gray-900 dark:bg-blue-600 text-white rounded-xl hover:bg-gray-800 dark:hover:bg-blue-700 transition-all text-xs sm:text-sm font-semibold shadow-md"
                >
                  <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">Add Card</span>
                  <span className="xs:hidden">Add</span>
                </button>
              </div>

              <div className="p-4 sm:p-6">
                {paymentCards.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 dark:text-gray-600" />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      No payment methods
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
                      Add a payment method to complete your purchases
                    </p>
                    <button
                      onClick={() => setShowAddCard(true)}
                      className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold text-sm sm:text-base shadow-lg"
                    >
                      <Plus className="w-4 h-4" />
                      Add Payment Method
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentCards.map((card) => (
                      <div
                        key={card.id}
                        className={`p-4 sm:p-5 rounded-xl border-2 transition-all ${
                          card.isDefault
                            ? 'border-blue-500 dark:border-blue-600 bg-blue-50/50 dark:bg-blue-900/10'
                            : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                            <div className={`w-12 h-9 sm:w-14 sm:h-10 rounded-lg flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 ${
                              card.brand === 'Visa' 
                                ? 'bg-blue-600 text-white' 
                                : card.brand === 'Mastercard'
                                ? 'bg-red-600 text-white'
                                : card.brand === 'Amex'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-700 text-white'
                            }`}>
                              {card.brand}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm sm:text-base font-bold text-gray-900 dark:text-white mb-1 truncate">
                                •••• •••• •••• {card.last4}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                Expires {card.expiryMonth}/{card.expiryYear}
                              </p>
                              {card.isDefault && (
                                <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 sm:px-2.5 sm:py-1 bg-blue-500/10 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 text-xs font-semibold rounded-lg">
                                  <Check className="w-3 h-3" />
                                  Default
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                            {!card.isDefault && (
                              <button
                                onClick={() => handleSetDefaultCard(card.id)}
                                className="text-xs font-semibold text-blue-600 dark:text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 transition-colors px-2 sm:px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                              >
                                Set Default
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteCard(card.id)}
                              className="p-1.5 sm:p-2 text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Card Form */}
                {showAddCard && (
                  <div className="mt-4 sm:mt-6 p-4 sm:p-5 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white mb-4">
                      Add New Card
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      <input
                        type="text"
                        placeholder="Card Number"
                        value={newCard.cardNumber}
                        onChange={(e) => setNewCard({ ...newCard, cardNumber: formatCardNumber(e.target.value) })}
                        maxLength={19}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base text-gray-900 dark:text-white placeholder:text-gray-400"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="MM/YY"
                          value={newCard.expiryDate}
                          onChange={(e) => setNewCard({ ...newCard, expiryDate: formatExpiryDate(e.target.value) })}
                          maxLength={5}
                          className="px-3 sm:px-4 py-2.5 sm:py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base text-gray-900 dark:text-white placeholder:text-gray-400"
                        />
                        <input
                          type="text"
                          placeholder="CVV"
                          value={newCard.cvv}
                          onChange={(e) => setNewCard({ ...newCard, cvv: e.target.value.replace(/\D/g, '') })}
                          maxLength={4}
                          className="px-3 sm:px-4 py-2.5 sm:py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base text-gray-900 dark:text-white placeholder:text-gray-400"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={handleAddCard}
                          className="px-4 py-2.5 sm:py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm sm:text-base"
                        >
                          Add Card
                        </button>
                        <button
                          onClick={() => {
                            setShowAddCard(false);
                            setNewCard({ cardNumber: '', expiryDate: '', cvv: '' });
                          }}
                          className="px-4 py-2.5 sm:py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-semibold text-sm sm:text-base"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountPage;